import { useState } from "react";
import { ErrorOutline } from "@mui/icons-material";

import { useTranslation } from "@/lib/i18n";

import { collectErrorDetails } from "./collect-error-details";
import styles from "./widget-error.module.css";

type WidgetErrorProps = {
  /** Required human-readable message shown next to the icon. */
  message: string;
  /** Optional rich error; its message and `cause` chain populate the reveal. */
  error?: unknown;
};

/**
 * Error state for a dashboard widget: a centered icon with a human message and,
 * when a rich `error` is supplied, a toggle that reveals the underlying message
 * and its `cause` chain. Renders in place of the widget body.
 */
export function WidgetError({ message, error }: WidgetErrorProps) {
  const { t } = useTranslation();
  const [showDetails, setShowDetails] = useState(false);

  const details = collectErrorDetails(error);
  const hasDetails = details.length > 0;

  return (
    <div className={styles.root}>
      <ErrorOutline className={styles.icon} fontSize="large" />
      <p className={styles.message}>{message}</p>

      {hasDetails && (
        <button
          type="button"
          className={styles.detailsToggle}
          onClick={() => setShowDetails((previous) => !previous)}
        >
          {showDetails ? t("widgets.error.hide_details") : t("widgets.error.show_details")}
        </button>
      )}

      {hasDetails && showDetails && (
        <ul className={styles.details}>
          {details.map((detail, index) => (
            <li key={index} className={styles.detailItem}>
              {detail}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
